import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocationFormDialog } from '@/components/shared/LocationFormDialog';
import { useDeleteLocation } from '@/hooks/useLocations';
import type { Location } from '@jobos/shared/types/location';
import { clientKeys } from '@/hooks/useClients';
import { Pencil, Trash2 } from 'lucide-react';

interface LocationsSectionProps {
  clientId: string;
  locations: Location[];
}

export function LocationsSection({ clientId, locations }: LocationsSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { mutateAsync: deleteLocation } = useDeleteLocation();

  const handleOpenCreate = () => {
    setEditingLocation(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (location: Location) => {
    console.log('Editing location:', location);
    setEditingLocation(location);
    setIsModalOpen(true);
  };

  const handleSuccess = () => {
    // Refresh client data to show updated/new location
    queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) });
    setEditingLocation(null);
  };

  const handleDeleteLocation = async (location: Location) => {
    const locationName = location.name || location.addressLine1 || 'this location';
    if (confirm(`Are you sure you want to delete ${locationName}?`)) {
      try {
        await deleteLocation(location.id);
        // Refresh client data to remove deleted location
        queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) });
      } catch (error) {
        console.error('Failed to delete location:', error);
      }
    }
  };

  return (
    <>
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Service Addresses ({locations.length})
            </h2>
            <Button size="sm" onClick={handleOpenCreate}>
              Add Service Address
            </Button>
          </div>

          {locations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>ZIP</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((location) => (
                  <TableRow
                    key={location.id}
                    onClick={() => navigate(`/locations/${location.id}`)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <TableCell>
                      {location.name && <div className="font-medium">{location.name}</div>}
                      {location.addressLine1}
                      {location.addressLine2 && `, ${location.addressLine2}`}
                    </TableCell>
                    <TableCell>{location.city}</TableCell>
                    <TableCell>{location.state}</TableCell>
                    <TableCell>{location.zip}</TableCell>
                    <TableCell>{location.manager?.name}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(location);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLocation(location);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No service addresses found for this client</p>
              <Button className="mt-4" size="sm" onClick={handleOpenCreate}>
                Add First Service Address
              </Button>
            </div>
          )}
        </div>
      </div>

      <LocationFormDialog
        key={editingLocation?.id || 'new'}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSuccess={handleSuccess}
        clientId={clientId}
        location={editingLocation}
      />
    </>
  );
}
